/**
 * Opciones de orden del listado de flats.
 *
 * Cada opción apunta a una réplica estándar del índice de flats, creadas por el
 * backend en `ensureSortReplicas` (`DBBussinessLayer/algolia.js`) — los sufijos
 * de aquí y los de allí tienen que coincidir. "Relevancia" no tiene réplica: es
 * el índice principal, es decir el orden que el listado ha servido siempre.
 *
 * El precio ordena por `precioOrden`, un atributo numérico unificado que el
 * backend calcula según el régimen (`importeVenta` en venta,
 * `importeRecibofinal` en alquiler y media estancia). Como cada listado filtra
 * por un único `ventaAlquiler`, las mismas dos réplicas de precio sirven para
 * los tres tipos.
 */

export type FlatSortOption = {
  /** Slug estable para la URL (`?orden=precio-asc`). */
  slug: string;
  label: string;
  /** Sufijo de la réplica, o `null` para el índice principal. */
  suffix: string | null;
};

/** El primero es el default: sin `?orden`, el listado usa el índice principal. */
export const FLAT_SORT_OPTIONS: FlatSortOption[] = [
  { slug: 'relevancia', label: 'Relevancia', suffix: null },
  { slug: 'precio-asc', label: 'Precio: menor a mayor', suffix: 'precio_asc' },
  {
    slug: 'precio-desc',
    label: 'Precio: mayor a menor',
    suffix: 'precio_desc',
  },
  { slug: 'tamano-desc', label: 'Tamaño: mayor primero', suffix: 'm2_desc' },
  { slug: 'novedades', label: 'Novedades primero', suffix: 'nuevo_desc' },
];

export const DEFAULT_FLAT_SORT = FLAT_SORT_OPTIONS[0];

const indexFor = (indexName: string, { suffix }: FlatSortOption) =>
  suffix ? `${indexName}_${suffix}` : indexName;

/** `items` para `useSortBy`: el `value` es el nombre real del índice. */
export const flatSortItems = (indexName: string) =>
  FLAT_SORT_OPTIONS.map((option) => ({
    label: option.label,
    value: indexFor(indexName, option),
  }));

/**
 * Índice efectivo para un slug de URL. Devuelve el índice principal ante un
 * slug desconocido — un `?orden` inventado degrada a relevancia en vez de
 * apuntar a un índice inexistente (que daría 404 de Algolia).
 */
export const flatSortIndexForSlug = (
  indexName: string,
  slug?: string | null,
) => {
  const option = FLAT_SORT_OPTIONS.find((o) => o.slug === slug);
  return indexFor(indexName, option ?? DEFAULT_FLAT_SORT);
};

/** Inverso: slug para un nombre de índice. `null` si es el orden por defecto. */
export const flatSortSlugForIndex = (
  indexName: string,
  value?: string | null,
) => {
  if (!value || value === indexName) return null;
  const option = FLAT_SORT_OPTIONS.find(
    (o) => o.suffix && indexFor(indexName, o) === value,
  );
  return option?.slug ?? null;
};
